import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Settings, Mail, TestTube, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'instantly', label: 'Instantly' },
  { key: 'sendgrid', label: 'SendGrid' },
  { key: 'ses', label: 'AWS SES' },
  { key: 'deliverability', label: 'Deliverability' },
];

export default function EmailProviderSettingsCard() {
  const [activeTab, setActiveTab] = useState('instantly');
  const [providers, setProviders] = useState({});
  const [deliverability, setDeliverability] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [provRes, delRes] = await Promise.all([
        axios.get(`${API}/api/admin/settings/email-providers`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/api/admin/settings/deliverability`, { withCredentials: true }).catch(() => null),
      ]);
      if (provRes?.data) setProviders(provRes.data);
      if (delRes?.data) setDeliverability(delRes.data);
    } catch (e) {
      console.error('Failed to load provider settings', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveProvider = async (provider, data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings/email-providers/${provider}`, data, { withCredentials: true });
      toast.success(`${provider} settings saved`);
      fetchAll();
    } catch (e) {
      toast.error(`Failed to save ${provider} settings`);
    } finally {
      setSaving(false);
    }
  };

  const testProvider = async (provider) => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/api/admin/settings/email-providers/${provider}/test`, {}, { withCredentials: true });
      if (res.data?.ok) {
        toast.success(`${provider} test passed`);
      } else {
        toast.error(`${provider} test failed: ${res.data?.error || 'unknown'}`);
      }
    } catch (e) {
      toast.error(`Test failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setTesting(false);
    }
  };

  const saveDeliverability = async (data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings/deliverability`, data, { withCredentials: true });
      toast.success('Deliverability settings saved');
    } catch (e) {
      toast.error('Failed to save deliverability settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-md bg-blue-100">
            <Mail className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <CardTitle className="text-base">Email Providers</CardTitle>
            <CardDescription>Configure sending providers for email campaigns</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex-1 text-xs font-medium py-1.5 px-2 rounded-md transition-all ${
                activeTab === t.key ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Instantly Tab */}
        {activeTab === 'instantly' && (
          <InstantlyTab
            config={providers.instantly || {}}
            onSave={(data) => saveProvider('instantly', data)}
            onTest={() => testProvider('instantly')}
            saving={saving}
            testing={testing}
          />
        )}

        {/* SendGrid Tab */}
        {activeTab === 'sendgrid' && (
          <ProviderForm
            provider="sendgrid"
            config={providers.sendgrid || {}}
            onSave={(data) => saveProvider('sendgrid', data)}
            onTest={() => testProvider('sendgrid')}
            saving={saving}
            testing={testing}
            fields={[
              { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'SG.xxxx' },
              { key: 'from_email', label: 'From Email', placeholder: 'outreach@company.com' },
              { key: 'from_name', label: 'From Name', placeholder: 'Sales Team' },
              { key: 'reply_to', label: 'Reply-To Email', placeholder: 'reply@company.com' },
              { key: 'daily_limit', label: 'Daily Send Limit', type: 'number', placeholder: '5000' },
            ]}
          />
        )}

        {/* SES Tab */}
        {activeTab === 'ses' && (
          <ProviderForm
            provider="ses"
            config={providers.ses || {}}
            onSave={(data) => saveProvider('ses', data)}
            onTest={() => testProvider('ses')}
            saving={saving}
            testing={testing}
            fields={[
              { key: 'aws_access_key_id', label: 'AWS Access Key ID', placeholder: 'AKIA...' },
              { key: 'aws_secret_access_key', label: 'AWS Secret Key', type: 'password', placeholder: 'secret' },
              { key: 'aws_region', label: 'AWS Region', placeholder: 'us-east-1' },
              { key: 'from_email', label: 'From Email', placeholder: 'outreach@company.com' },
              { key: 'from_name', label: 'From Name', placeholder: 'Sales Team' },
              { key: 'reply_to', label: 'Reply-To Email', placeholder: 'reply@company.com' },
              { key: 'configuration_set', label: 'Configuration Set (optional)', placeholder: '' },
              { key: 'daily_limit', label: 'Daily Send Limit', type: 'number', placeholder: '5000' },
            ]}
          />
        )}

        {/* Deliverability Tab */}
        {activeTab === 'deliverability' && (
          <DeliverabilityTab
            config={deliverability}
            onSave={saveDeliverability}
            saving={saving}
          />
        )}
      </CardContent>
    </Card>
  );
}


function InstantlyTab({ config, onSave, onTest, saving, testing }) {
  const [apiKey, setApiKey] = useState(config.api_key || '');
  const [enabled, setEnabled] = useState(config.enabled !== false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Enabled</Label>
        <Switch checked={enabled} onCheckedChange={setEnabled} />
      </div>
      <div>
        <Label className="text-xs text-slate-500">API Key (overrides .env if set)</Label>
        <Input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="Leave blank to use .env INSTANTLY_API_KEY" />
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onSave({ api_key: apiKey, enabled })} disabled={saving}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Settings className="h-3 w-3 mr-1" />}
          Save
        </Button>
        <Button size="sm" variant="outline" onClick={onTest} disabled={testing}>
          {testing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <TestTube className="h-3 w-3 mr-1" />}
          Test Connection
        </Button>
      </div>
    </div>
  );
}


function ProviderForm({ provider, config, onSave, onTest, saving, testing, fields }) {
  const [form, setForm] = useState(() => {
    const initial = { enabled: config.enabled || false };
    fields.forEach(f => { initial[f.key] = config[f.key] || ''; });
    return initial;
  });

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Enabled</Label>
        <Switch checked={form.enabled} onCheckedChange={v => update('enabled', v)} />
      </div>
      {fields.map(f => (
        <div key={f.key}>
          <Label className="text-xs text-slate-500">{f.label}</Label>
          <Input
            type={f.type || 'text'}
            value={form[f.key]}
            onChange={e => update(f.key, f.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value)}
            placeholder={f.placeholder}
          />
        </div>
      ))}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onSave(form)} disabled={saving}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Settings className="h-3 w-3 mr-1" />}
          Save
        </Button>
        <Button size="sm" variant="outline" onClick={onTest} disabled={testing}>
          {testing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <TestTube className="h-3 w-3 mr-1" />}
          Send Test Email
        </Button>
      </div>
    </div>
  );
}


function DeliverabilityTab({ config, onSave, saving }) {
  const [form, setForm] = useState({
    throttle_enabled: config.throttle_enabled !== false,
    throttle_interval_seconds: config.throttle_interval_seconds || 8,
    spread_hours: config.spread_hours || 6,
    bounce_threshold_percent: config.bounce_threshold_percent || 5,
    bounce_check_window_hours: config.bounce_check_window_hours || 24,
    unsubscribe_link_enabled: config.unsubscribe_link_enabled !== false,
    unsubscribe_url: config.unsubscribe_url || '',
    reply_to: config.reply_to || '',
  });

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label>Send Throttling</Label>
          <p className="text-xs text-slate-500">Drip emails over time instead of blasting</p>
        </div>
        <Switch checked={form.throttle_enabled} onCheckedChange={v => update('throttle_enabled', v)} />
      </div>
      {form.throttle_enabled && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Min Interval (seconds)</Label>
            <Input type="number" value={form.throttle_interval_seconds} onChange={e => update('throttle_interval_seconds', parseInt(e.target.value) || 8)} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Spread Over (hours)</Label>
            <Input type="number" value={form.spread_hours} onChange={e => update('spread_hours', parseInt(e.target.value) || 6)} />
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-500">Bounce Threshold (%)</Label>
          <Input type="number" value={form.bounce_threshold_percent} onChange={e => update('bounce_threshold_percent', parseInt(e.target.value) || 5)} />
          <p className="text-[10px] text-slate-400 mt-0.5">Auto-pause campaign if exceeded</p>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Check Window (hours)</Label>
          <Input type="number" value={form.bounce_check_window_hours} onChange={e => update('bounce_check_window_hours', parseInt(e.target.value) || 24)} />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <Label>Unsubscribe Link</Label>
          <p className="text-xs text-slate-500">Add unsubscribe link to SendGrid/SES emails</p>
        </div>
        <Switch checked={form.unsubscribe_link_enabled} onCheckedChange={v => update('unsubscribe_link_enabled', v)} />
      </div>
      {form.unsubscribe_link_enabled && (
        <div>
          <Label className="text-xs text-slate-500">Unsubscribe URL Base</Label>
          <Input value={form.unsubscribe_url} onChange={e => update('unsubscribe_url', e.target.value)} placeholder="https://crm.aptask.com/api/email-providers/unsubscribe" />
        </div>
      )}
      <div>
        <Label className="text-xs text-slate-500">Global Reply-To</Label>
        <Input value={form.reply_to} onChange={e => update('reply_to', e.target.value)} placeholder="reply@company.com" />
      </div>
      <Button size="sm" onClick={() => onSave(form)} disabled={saving}>
        {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Settings className="h-3 w-3 mr-1" />}
        Save Deliverability Settings
      </Button>
    </div>
  );
}
